import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Modal from '../components/Modal';
import PrepwiseLogo from '../components/PrepwiseLogo';
import { GoogleLoginButton } from '../components/ConditionalGoogleLogin';
import './AdminSignUp.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface PendingInvite {
  id: string;
  email: string;
  organizationId: string;
  organizationName: string;
  role: string;
  status: string;
}

const AdminSignUp: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  // Modal state
  const [modal, setModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  const [isRegistering, setIsRegistering] = useState(false);

  // Check for pending invite and accept it
  const checkAndAcceptInvite = async (email: string): Promise<PendingInvite | null> => {
    try {
      const response = await fetch(`${API_URL}/auth/pending-invite?email=${encodeURIComponent(email)}`);
      if (!response.ok) return null;
      
      const data = await response.json();
      if (!data.invite || data.invite.status !== 'pending') return null;
      
      // Accept the invite
      const acceptResponse = await fetch(
        `${API_URL}/auth/accept-invite/${data.invite.id}?email=${encodeURIComponent(email)}`,
        { method: 'POST' }
      );
      
      if (!acceptResponse.ok) return null;
      
      return data.invite as PendingInvite;
    } catch (error) {
      console.error('Error checking invite:', error);
      return null;
    }
  };

  const handleGoogleSuccess = async (tokenResponse: any) => {
    setIsRegistering(true);
    try {
      // Fetch user info from Google
      const userInfoResponse = await fetch(
        'https://www.googleapis.com/oauth2/v3/userinfo',
        {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        }
      );
      
      if (!userInfoResponse.ok) {
        throw new Error('Failed to fetch user info');
      }
      
      const userInfo = await userInfoResponse.json();
      
      // Check if user has a pending invite
      const invite = await checkAndAcceptInvite(userInfo.email);
      
      if (invite) {
        // User has an invite - they're being added to an existing org
        // Login with the role from the invite
        await login({
          id: userInfo.sub,
          email: userInfo.email,
          name: userInfo.name,
          picture: userInfo.picture,
        });
        
        setModal({
          isOpen: true,
          title: 'Welcome!',
          message: `You've been added to ${invite.organizationName} as ${invite.role}. Redirecting...`,
          type: 'success',
        });
        
        // Navigate based on role
        setTimeout(() => {
          if (invite.role === 'admin') {
            navigate('/admin/dashboard');
          } else {
            navigate('/employee/dashboard');
          }
        }, 2000);
      } else {
        // No invite - register as a new user but redirect to waiting page
        const registerResponse = await fetch(`${API_URL}/auth/register-admin`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: userInfo.email,
            name: userInfo.name,
            id: userInfo.sub,
          }),
        });

        if (!registerResponse.ok) {
          const errorData = await registerResponse.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to register');
        }

        // Login with user info
        await login({
          id: userInfo.sub,
          email: userInfo.email,
          name: userInfo.name,
          picture: userInfo.picture,
        });
        
        // Redirect to waiting page since they have no org
        setModal({
          isOpen: true,
          title: 'Account Created',
          message: 'Your account has been created. Please wait for an organization admin to invite you.',
          type: 'info',
        });

        setTimeout(() => {
          navigate('/waiting-for-invite');
        }, 2000);
      }
    } catch (error: any) {
      console.error('Sign up error:', error);
      setModal({
        isOpen: true,
        title: 'Registration Failed',
        message: error.message || 'Failed to create account. Please try again.',
        type: 'error',
      });
    } finally {
      setIsRegistering(false);
    }
  };

  const handleGoogleError = () => {
    setIsRegistering(false);
    setModal({
      isOpen: true,
      title: 'Registration Failed',
      message: 'Google sign-up failed. Please try again.',
      type: 'error',
    });
  };

  return (
    <div className="admin-signup-container">
      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
      
      <div className="admin-signup-card">
        <div className="admin-signup-header">
          <div className="admin-signup-logo-container">
            <PrepwiseLogo size="large" />
            <h1>Prepwise</h1>
          </div>
          <p className="admin-signup-subtitle">Sign Up</p>
          <p className="admin-signup-description">
            Sign up with Google. If you've been invited to an organization, 
            you'll be automatically added. Otherwise, you'll need to wait for an invite.
          </p>
        </div>
        
        <div className="admin-signup-options">
          {GOOGLE_CLIENT_ID ? (
            <>
              <GoogleLoginButton
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                className={`admin-signup-button google-signup ${isRegistering ? 'disabled' : ''}`}
              >
                {isRegistering ? (
                  <>
                    <span className="spinner"></span>
                    Creating Account...
                  </>
                ) : (
                  <>
                    <svg className="google-icon" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    Sign Up with Google
                  </>
                )}
              </GoogleLoginButton>
              
              <div className="admin-signup-divider">
                <span>or</span>
              </div>
            </>
          ) : (
            <div className="admin-signup-notice">
              <p>Google OAuth is not configured for local development.</p>
              <Link to="/dev/login" className="dev-login-link">
                Use Dev Login Instead →
              </Link>
            </div>
          )}
        </div>

        <div className="admin-signup-footer">
          <p>
            Already have an account?{' '}
            <Link to="/login" className="admin-signup-link">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminSignUp;
